"use client";

import { useRouter } from "next/navigation";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./dialog.js";
import { Button } from "./button.js";
import { Card, CardContent } from "./card.js";
import { Settings, Calendar, AlertTriangle, ArrowRight } from "lucide-react";

interface AccountRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

export const AccountRequiredModal = ({ 
  isOpen, 
  onClose, 
  title = "Calendar Setup Required",
  description = "You need to connect your calendar accounts before using this feature."
}: AccountRequiredModalProps) => {
  const router = useRouter();

  const handleGoToSetup = () => {
    router.push('/unifiedtime/setup');
    onClose();
  };

  const handleStayHere = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="flex items-start space-x-3">
                <Calendar className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-800">Why do I need to connect calendars?</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Connect Google Calendar or Microsoft Outlook to merge external events. If you use
                    WTC Email (mail.westtn.consulting), your mail-app calendar can appear on the unified
                    calendar without Google or Microsoft.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Button 
              onClick={handleGoToSetup}
              className="w-full flex items-center justify-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Go to Setup Page
              <ArrowRight className="h-4 w-4" />
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleStayHere}
              className="w-full"
            >
              Stay on This Page
            </Button>
          </div>

          <div className="text-xs text-gray-500 text-center">
            You can always access the setup page from the main UnifiedTime menu.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
